/**
 * Assistant IA de l'espace pédagogique (cahier des charges oral du 23/09/2026, puis version 7).
 * Version 7 : plus aucune « trame » sans IA — toute génération passe par l'assistant. Quand il n'est pas configuré,
 * les boutons de génération sont désactivés et une alerte explique où le régler (Organisme → Assistant IA).
 * L'IA ne remplit qu'un BROUILLON dans le formulaire : le formateur relit, corrige, puis enregistre lui-même.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Sparkles } from "lucide-react";
import { NOMBRE_QUESTIONS_DEFAUT } from "@/domaine/pedagogie/listes";
import { api, ErreurApi, type EtatGenerationDoc, type EtatIa } from "../api";
import { Alerte, Bouton, cx } from "../ui/base";

/** État de l'assistant IA de l'organisme : disponible ? avec quel moteur ? (mis en cache cinq minutes). */
export function useEtatIa(): { disponible: boolean; description: string; chargement: boolean } {
  const etat = useQuery({ queryKey: ["ia-etat"], queryFn: () => api.get<EtatIa>("/ia/etat"), staleTime: 5 * 60_000 });
  return { disponible: etat.data?.disponible === true, description: etat.data?.description ?? "", chargement: etat.isPending };
}

export function useIaDisponible(): boolean {
  return useEtatIa().disponible;
}

/** Alerte affichée à la place d'un générateur quand l'assistant n'est pas configuré. */
export function AlerteIaIndisponible() {
  return (
    <Alerte ton="attention" titre="L'assistant IA n'est pas configuré">
      L'administrateur de l'organisme le règle dans <strong>Organisme → Assistant IA</strong>. En attendant, la génération est indisponible : vous pouvez tout rédiger à la main.
    </Alerte>
  );
}

/** Mention discrète du moteur, à côté d'un bouton de génération (ex. « Sonnet 5 + recherche web »). */
export function MentionMoteur({ description, className }: { description: string; className?: string }) {
  if (!description) return null;
  return (
    <span className={["inline-flex items-center gap-1.5 text-[12.5px] text-encre-3", className].filter(Boolean).join(" ")}>
      <Sparkles className="size-3.5" aria-hidden />
      {description}
    </span>
  );
}

type Question = { enonce: string; propositions: string[]; bonne_reponse: number };

// ——— Générations longues en tâche de fond (31/10/2026) ———

export type EtatTacheIa<T> = {
  id: string;
  statut: "en_cours" | "termine" | "echec";
  duree_ms: number;
  progression: { pourcentage: number; fait: number; total: number; etape: string };
  resultat?: T;
  erreur?: { message: string; erreurs?: string[] };
};

/**
 * Lance une génération IA en tâche de fond (`POST /ia/taches`) puis sonde son état toutes les 1,5 s : la requête
 * HTTP ne dure plus le temps de la génération, et l'écran montre où en est le serveur (« Module 2/6 », temps écoulé).
 */
export function useTacheIa<T>(surFin?: (resultat: T) => void) {
  const [id, setId] = useState<string | null>(null);
  const [debut, setDebut] = useState<number | null>(null);
  const [maintenant, setMaintenant] = useState(() => Date.now());
  const recu = useRef<string | null>(null);
  const requetes = useQueryClient();
  const lancer = useMutation({
    mutationFn: (corps: Record<string, unknown>) => api.post<{ tache_id: string }>("/ia/taches", corps),
    onMutate: () => { setId(null); setDebut(Date.now()); recu.current = null; },
    onSuccess: (r) => setId(r.tache_id),
  });
  const etat = useQuery({
    queryKey: ["ia-tache", id],
    queryFn: () => api.get<EtatTacheIa<T>>(`/ia/taches/${id}`),
    enabled: id !== null,
    refetchInterval: (q) => (q.state.data && q.state.data.statut !== "en_cours" ? false : 1500),
    retry: 1,
  });
  const enCours = lancer.isPending || (id !== null && !etat.error && (!etat.data || etat.data.statut === "en_cours"));
  useEffect(() => {
    if (!enCours) return;
    const minuterie = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => clearInterval(minuterie);
  }, [enCours]);
  const fin = etat.data?.statut === "termine" ? etat.data : null;
  useEffect(() => {
    if (fin && id && recu.current !== id) {
      recu.current = id;
      // Essais et verrous ont changé : l'écran les relit.
      void requetes.invalidateQueries({ queryKey: ["generations"] });
      surFin?.(fin.resultat as T);
    }
  }, [fin, id, surFin, requetes]);
  // Le pourcentage affiché ne recule jamais (même entre deux sondages).
  const maxAffiche = useRef(0);
  const progression = etat.data?.progression ?? null;
  if (!enCours && !fin) maxAffiche.current = 0;
  const pourcentage = progression ? Math.max(maxAffiche.current, progression.pourcentage) : maxAffiche.current;
  maxAffiche.current = pourcentage;
  const erreurApi = lancer.error ?? etat.error;
  const erreur = erreurApi ? erreurApi.message : etat.data?.statut === "echec" ? (etat.data.erreur?.message ?? "La génération a échoué.") : null;
  const erreurs = erreurApi instanceof ErreurApi && Array.isArray(erreurApi.details?.erreurs) ? (erreurApi.details.erreurs as string[]) : (etat.data?.erreur?.erreurs ?? []);
  return {
    lancer: (corps: Record<string, unknown>) => lancer.mutate(corps),
    enCours,
    progression: progression ? { ...progression, pourcentage } : null,
    ecoule_ms: debut === null ? 0 : (fin ? fin.duree_ms : Math.max(0, maintenant - debut)),
    resultat: fin ? (fin.resultat as T) : null,
    erreur,
    erreurs,
    reinitialiser: () => { setId(null); setDebut(null); lancer.reset(); },
  };
}

const minutes = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * Progression d'une génération (31/10/2026) : barre, pourcentage (« 45 % »), étape en clair, temps écoulé. Le
 * pourcentage vient du serveur : il avance par étapes réelles, en douceur dans une étape longue, jamais 100 % avant la fin.
 */
export function ProgressionTache({ tache, libelle }: { tache: ReturnType<typeof useTacheIa<unknown>>; libelle: string }) {
  if (!tache.enCours) return null;
  const p = tache.progression;
  const part = p?.pourcentage ?? 0;
  return (
    <div role="status" aria-live="polite" className="space-y-1.5 rounded-md border border-trait bg-papier-2 px-4 py-3 text-sm">
      <p className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{libelle}{p && p.total > 1 ? ` — ${p.fait}/${p.total}` : ""}</span>
        <span className="chiffres text-[12.5px] text-encre-3">
          <strong className="mr-2 text-[15px] text-accent" data-testid="pourcentage">{part} %</strong>
          {minutes(tache.ecoule_ms)} écoulée(s)
        </span>
      </p>
      <div className="h-2 overflow-hidden rounded-full bg-papier-3" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={part} aria-label={`${libelle} : ${part} %`}>
        <div className={cx("h-full rounded-full bg-accent transition-[width] duration-700 ease-out", part === 0 && "w-1/12 animate-pulse")} style={part > 0 ? { width: `${Math.max(part, 3)}%` } : undefined} />
      </div>
      <p className="text-[12.5px] text-encre-3">{p?.etape ?? "Démarrage…"} · la génération se poursuit sur le serveur.</p>
    </div>
  );
}

// ——— Verrou et plafond des générations (31/10/2026) ———

/** État du verrou et du plafond de chaque document de la formation. */
export function useGenerations(formationId: string | undefined) {
  const q = useQuery({ queryKey: ["generations", formationId], queryFn: () => api.get<EtatGenerationDoc[]>(`/formations/${formationId}/generations`), enabled: Boolean(formationId) });
  return { etats: q.data ?? [], de: (cle: string) => q.data?.find((e) => e.cle === cle) ?? null };
}

const dateCourte = (iso: string) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

/** Une génération est-elle refusée (verrouillée ou au plafond) ? */
export const generationBloquee = (e: EtatGenerationDoc | null) => Boolean(e && (e.verrouille || e.essais_restants <= 0));

/**
 * Mention à la place (ou à côté) d'un bouton de génération : « Document produit le … — génération verrouillée »,
 * « Plafond d'essais atteint », sinon « 2 essais restants ».
 */
export function MentionVerrou({ etat, className }: { etat: EtatGenerationDoc | null; className?: string }) {
  if (!etat) return null;
  if (etat.verrouille)
    return (
      <span className={cx("inline-flex items-center gap-1.5 rounded-sm border border-trait bg-papier-2 px-2.5 py-1 text-[12.5px] font-medium text-encre-2", className)} data-testid="verrou">
        <Lock className="size-3.5" aria-hidden />
        Document produit le {dateCourte(etat.produit_le!)} — génération verrouillée
      </span>
    );
  if (etat.essais_restants <= 0)
    return (
      <span className={cx("inline-flex items-center gap-1.5 text-[12.5px] font-medium text-danger", className)}>
        <Lock className="size-3.5" aria-hidden /> Plafond de générations atteint : enregistrez une proposition, puis modifiez-la à la main
      </span>
    );
  return <span className={cx("text-[12.5px] text-encre-3", className)}>{etat.essais_restants === 1 ? "1 essai restant" : `${etat.essais_restants} essais restants`} avant enregistrement</span>;
}

export function BoutonIaQcm({ formationId, type, recevoir }: { formationId: string; type: "positionnement" | "acquis"; recevoir: (q: { titre: string; questions: Question[] }) => void }) {
  const ia = useEtatIa();
  const proposer = useTacheIa<{ questionnaire: { titre: string; questions: Question[] } }>((r) => recevoir(r.questionnaire));
  const verrou = useGenerations(formationId || undefined).de(`qcm:${type}`);
  if (ia.chargement) return null;
  if (!ia.disponible) return <AlerteIaIndisponible />;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <Bouton variante="secondaire" taille="sm" icone={<Sparkles className="size-3.5" aria-hidden />} disabled={!formationId || proposer.enCours || generationBloquee(verrou)} title={formationId ? undefined : "Rattachez d'abord le modèle à une formation"} onClick={() => proposer.lancer({ type: "qcm", formation_id: formationId, qcm_type: type, nombre: NOMBRE_QUESTIONS_DEFAUT[type] })}>
          Proposer 10 questions avec l'IA
        </Bouton>
        <MentionMoteur description={ia.description} />
        <MentionVerrou etat={verrou} />
      </div>
      <ProgressionTache tache={proposer} libelle="Rédaction des 10 questions" />
      {proposer.resultat && <Alerte ton="attention" titre="Brouillon proposé par l'IA">Relisez chaque question et chaque bonne réponse avant d'enregistrer : vous en êtes l'auteur.</Alerte>}
      {proposer.erreur && <Alerte ton="danger" titre={proposer.erreur}>{proposer.erreurs.length > 0 && <ul className="list-disc pl-4">{proposer.erreurs.map((e) => <li key={e}>{e}</li>)}</ul>}</Alerte>}
    </div>
  );
}

export function BoutonIaProgramme({ contexte, recevoir }: { contexte: { formation_titre: string; formation_niveau: string; public_vise: string; formation_prerequis: string; formation_duree_heures_total: number | null }; recevoir: (p: { formation_objectifs: string; programme: string }) => void }) {
  const ia = useEtatIa();
  const proposer = useMutation({
    mutationFn: () => api.post<{ formation_objectifs: string; programme: string }>("/ia/programme", contexte),
    onSuccess: recevoir,
  });
  if (!ia.disponible) return null;
  const erreurs = proposer.error instanceof ErreurApi && Array.isArray(proposer.error.details?.erreurs) ? proposer.error.details.erreurs : [];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <Bouton variante="secondaire" taille="sm" icone={<Sparkles className="size-3.5" aria-hidden />} disabled={contexte.formation_titre.trim().length < 3} enCours={proposer.isPending} onClick={() => proposer.mutate()}>
          Proposer objectifs et programme avec l'IA
        </Bouton>
        <MentionMoteur description={ia.description} />
      </div>
      {proposer.isSuccess && <Alerte ton="attention" titre="Brouillon proposé par l'IA">Objectifs et programme ont été pré-remplis ci-dessous. Relisez et corrigez avant d'enregistrer.</Alerte>}
      {proposer.error && (
        <Alerte ton="danger" titre={proposer.error.message}>
          {erreurs.length > 0 && <ul className="list-disc pl-4">{erreurs.map((e) => <li key={e}>{e}</li>)}</ul>}
        </Alerte>
      )}
    </div>
  );
}
